import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ShieldAlert, Trash2, Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MemberLayout } from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [orgName, setOrgName] = useState("");
  const [locations, setLocations] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [prefs, setPrefs] = useState({ default_location_id: "", default_activity_type_id: "", default_count: 1 });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/locations").then(r => setLocations(r.data));
    api.get("/activity-types").then(r => setActivityTypes(r.data));
    if (user?.organization_id) {
      api.get("/organizations").then(r => {
        const o = r.data.find(x => x.id === user.organization_id);
        if (o) setOrgName(o.name);
      });
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      setPrefs({
        default_location_id: user.default_location_id || "",
        default_activity_type_id: user.default_activity_type_id || "",
        default_count: user.default_count || 1,
      });
    }
  }, [user]);

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      await api.patch("/users/me/preferences", prefs);
      await refresh();
      toast.success("Favoriter sparade");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunde inte spara");
    } finally { setSavingPrefs(false); }
  };

  const anonymize = async () => {
    setLoading(true);
    try {
      await api.post("/users/me/anonymize");
      localStorage.removeItem("access_token");
      toast.success("Ditt konto har anonymiserats");
      await refresh();
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunde inte anonymisera");
    } finally { setLoading(false); }
  };

  if (!user) return null;

  return (
    <MemberLayout title="Profil">
      {/* ----- Personal info ----- */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="profile-card">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Namn</div>
        <div className="text-lg font-semibold text-slate-900">{user.first_name} {user.last_name}</div>
        <div className="mt-4 text-xs uppercase tracking-widest text-slate-500 font-semibold">E-post</div>
        <div className="text-slate-900" data-testid="profile-email">{user.email}</div>
        {user.phone && (<><div className="mt-4 text-xs uppercase tracking-widest text-slate-500 font-semibold">Telefon</div><div className="text-slate-900">{user.phone}</div></>)}
        <div className="mt-4 text-xs uppercase tracking-widest text-slate-500 font-semibold">Arbetarekommun</div>
        <div className="text-slate-900" data-testid="profile-org">{orgName || "—"}</div>
        <div className="mt-4 text-xs uppercase tracking-widest text-slate-500 font-semibold">Roll</div>
        <div className="text-slate-900 capitalize">{user.role.replace("_", " ")}</div>
      </div>

      {/* ----- Quick-register favorites ----- */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6" data-testid="prefs-card">
        <div className="flex items-center gap-2 mb-1">
          <Zap size={16} className="text-[#E1251B]"/>
          <h3 className="font-heading font-bold text-slate-900">Snabbregistrering</h3>
        </div>
        <p className="text-sm text-slate-500 mb-5">Välj favoritplats och aktivitetstyp så kan du registrera samtal från hem-vyn med ett tryck.</p>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Favoritplats</Label>
            <Select value={prefs.default_location_id || "none"} onValueChange={v => setPrefs(p => ({ ...p, default_location_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="prefs-loc"><SelectValue placeholder="Ingen"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Ingen</SelectItem>
                {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Favorit-aktivitetstyp</Label>
            <Select value={prefs.default_activity_type_id || "none"} onValueChange={v => setPrefs(p => ({ ...p, default_activity_type_id: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="prefs-at"><SelectValue placeholder="Ingen"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Ingen</SelectItem>
                {activityTypes.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Antal samtal per tryck (standard)</Label>
            <Input data-testid="prefs-count" type="number" min={1} max={100} value={prefs.default_count}
              onChange={e => setPrefs(p => ({ ...p, default_count: Math.max(1, Math.min(100, parseInt(e.target.value || "1"))) }))}
              className="h-11"/>
          </div>
          <Button data-testid="prefs-save" onClick={savePrefs} disabled={savingPrefs}
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-full">
            {savingPrefs ? "Sparar…" : "Spara favoriter"}
          </Button>
        </div>
      </div>

      <Button data-testid="profile-logout" variant="outline" onClick={async () => { await logout(); nav("/login"); }}
        className="w-full mt-6 h-12 rounded-full"><LogOut className="mr-2" size={16}/> Logga ut</Button>

      {/* GDPR danger zone */}
      <div className="mt-10 border border-rose-200 bg-rose-50/50 rounded-2xl p-5" data-testid="gdpr-zone">
        <div className="flex items-start gap-3">
          <ShieldAlert className="text-rose-600 flex-shrink-0 mt-0.5" size={20}/>
          <div>
            <h3 className="font-heading font-bold text-slate-900">Radera & anonymisera konto</h3>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">Ditt namn, e-post och telefon tas bort direkt. Kontot inaktiveras och kan inte återställas. <span className="font-semibold">Aggregerad kampanjstatistik behålls avidentifierat</span> så att organisationens totalsiffror inte påverkas.</p>
          </div>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button data-testid="gdpr-trigger" variant="outline" className="mt-4 border-rose-300 text-rose-700 hover:bg-rose-100 hover:text-rose-800 rounded-full">
              <Trash2 size={14} className="mr-2"/> Anonymisera mitt konto
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Anonymisera kontot permanent?</AlertDialogTitle>
              <AlertDialogDescription>
                Denna åtgärd går inte att ångra. Alla personuppgifter tas bort. Kampanjstatistik som redan har rapporterats stannar kvar avidentifierat.
                <br/><br/>
                Skriv <span className="font-bold text-rose-700">ANONYMISERA</span> för att bekräfta:
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="my-2">
              <Label htmlFor="conf" className="sr-only">Bekräftelse</Label>
              <Input id="conf" data-testid="gdpr-confirm-input" value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="Skriv ANONYMISERA"/>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="gdpr-cancel" onClick={() => setConfirmText("")}>Avbryt</AlertDialogCancel>
              <AlertDialogAction data-testid="gdpr-confirm" disabled={confirmText !== "ANONYMISERA" || loading}
                onClick={anonymize} className="bg-rose-600 hover:bg-rose-700 text-white">
                {loading ? "Anonymiserar…" : "Ja, anonymisera"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MemberLayout>
  );
}
